import React, { useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { filteredNav } from "@/lib/adminNav";
import {
  LayoutDashboard, Home as HomeIcon, ShoppingBag, Users, Mail,
  UserCog, FileText, Image as ImageIcon, Settings, BarChart3, Database,
  ChevronDown, LogOut, Menu, X, ExternalLink,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";

const ICONS = {
  LayoutDashboard, Home: HomeIcon, ShoppingBag, Users, Mail,
  UserCog, FileText, ImageIcon, Settings, BarChart3, Database,
};

const AdminLayout = ({ children, title, actions }) => {
  const { user, authLoading, logout } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = useMemo(() => filteredNav(user), [user]);
  const [openGroups, setOpenGroups] = useState(() => {
    // by default expand groups that contain the current path
    const g = {};
    nav.forEach((grp) => {
      const active = grp.items.some((it) => location.pathname.startsWith(it.to.split("?")[0]));
      g[grp.group] = active;
    });
    return g;
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  if (authLoading) return <div className="p-24 text-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center">
        <div className="overline text-accent mb-2">Restricted</div>
        <h1 className="font-display text-4xl mb-4">Staff access only</h1>
        <p className="text-muted-foreground mb-6">This section is for staff members. If you believe this is a mistake, contact your admin.</p>
        <button onClick={() => navigate("/")} data-testid="back-home-btn" className="border border-border px-6 py-3 hover:bg-accent hover:text-accent-foreground transition-colors">Back to site</button>
      </div>
    );
  }

  const toggleGroup = (g) => setOpenGroups((prev) => ({ ...prev, [g]: !prev[g] }));

  const isActive = (to) => {
    const [path, query] = to.split("?");
    if (location.pathname !== path) return false;
    if (!query) return location.search === "" || location.pathname === path;
    const search = new URLSearchParams(location.search);
    const wanted = new URLSearchParams(query);
    for (const [k, v] of wanted.entries()) {
      if (search.get(k) !== v) return false;
    }
    return true;
  };

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-primary text-primary-foreground overflow-y-auto z-40 transform transition-transform ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="px-6 py-6 border-b border-primary-foreground/10 flex items-center justify-between">
          <Link to="/admin/dashboard" className="font-display text-xl tracking-tight">
            Dream Home
            <span className="block text-[10px] tracking-[0.2em] uppercase text-primary-foreground/60 mt-1">Studio · Admin</span>
          </Link>
          <button onClick={() => setMobileOpen(false)} className="lg:hidden" data-testid="close-mobile-sidebar">
            <X size={18} />
          </button>
        </div>

        <nav className="py-4">
          {nav.map((group) => {
            const Icon = ICONS[group.icon] || LayoutDashboard;
            const open = !!openGroups[group.group];
            return (
              <div key={group.group} className="mb-1">
                <button
                  onClick={() => toggleGroup(group.group)}
                  data-testid={`nav-group-${group.group.toLowerCase().replace(/\s+/g, "-")}`}
                  className="w-full flex items-center justify-between px-6 py-2.5 text-[13px] tracking-wide hover:bg-primary-foreground/5 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <Icon size={15} className="text-accent/80" />
                    <span className="font-medium">{group.group}</span>
                  </span>
                  <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="pb-2">
                    {group.items.map((it) => (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        onClick={() => setMobileOpen(false)}
                        data-testid={`nav-item-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={() =>
                          `flex items-center gap-2 pl-14 pr-6 py-2 text-[12.5px] transition-colors ${isActive(it.to) ? "bg-accent text-accent-foreground border-l-2 border-accent-foreground" : "text-primary-foreground/70 hover:bg-primary-foreground/5 border-l-2 border-transparent"}`
                        }
                      >
                        {it.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="mt-6 border-t border-primary-foreground/10 p-6">
          <div className="text-[11px] uppercase tracking-widest text-primary-foreground/50 mb-1">Signed in</div>
          <div className="text-sm font-medium truncate" data-testid="admin-user-name">{user.name}</div>
          <div className="text-xs text-primary-foreground/60 truncate">{user.email}</div>
          <div className="text-[10px] mt-1 text-accent uppercase tracking-widest">{user.role.replace("_", " ")}</div>
          <div className="mt-4 flex flex-col gap-2">
            <Link to="/" data-testid="view-site-btn" className="text-xs flex items-center gap-2 text-primary-foreground/70 hover:text-primary-foreground">
              <ExternalLink size={12} /> View site
            </Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="text-xs flex items-center gap-2 text-primary-foreground/70 hover:text-accent">
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between px-6 lg:px-10 py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => setMobileOpen(true)} className="lg:hidden" data-testid="open-mobile-sidebar">
                <Menu size={22} />
              </button>
              <h1 className="font-display text-2xl lg:text-3xl tracking-tight" data-testid="admin-page-title">{title || "Dashboard"}</h1>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell />
              {actions}
            </div>
          </div>
        </div>
        <div className="px-6 lg:px-10 py-8" data-testid="admin-content-area">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
